import React, { useState, useEffect } from 'react';
import { Card, Spinner, Badge, Alert, Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { API_URL } from "../../utils/constant";
import { faSearch, faGlobe, faShieldAlt, faInfoCircle, faNetworkWired, faClock, faFile, faLink, faServer, faTag, faBuilding, faExclamationTriangle, faTimes, faCopy, faCheckCircle, faChevronLeft, faChevronRight, faAngleDoubleLeft, faAngleDoubleRight } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

const ThreatIntelFeeds = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [feeds, setFeeds] = useState([]);
    const [feedsLoading, setFeedsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [hasMore, setHasMore] = useState(true);
    const [hasPrevious, setHasPrevious] = useState(false);
    const [cursor, setCursor] = useState(null);
    const [startCursor, setStartCursor] = useState(null);
    const [cursorHistory, setCursorHistory] = useState([]); // Track cursor history for back navigation
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [selectedIndicator, setSelectedIndicator] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [copiedField, setCopiedField] = useState(null);

    const fetchFeeds = async (cursorParam = null, append = false, isPrevious = false) => {
        if (!cursorParam && !append) {
            setFeedsLoading(true);
        } else {
            setIsLoadingMore(true);
        }
        
        try {
            let url = `${API_URL}/v2/threat-intel/indicators?limit=25`;
            if (cursorParam) {
                url += `&cursor=${encodeURIComponent(cursorParam)}`;
            }
            // Add search parameter if search term exists
            if (searchTerm && searchTerm.trim()) {
                url += `&searchValue=${encodeURIComponent(searchTerm.trim())}`;
            }
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('session')}`,
                    'Content-Type': 'application/json'
                }
            });
            
            const data = await response.json();

            if (data.error) {
                throw new Error(data.userMessage || 'Failed to fetch feeds');
            }

            const newFeeds = data.data?.indicators || [];
            
            setFeeds(newFeeds);

            // Update cursor and pagination info
            const pagination = data.data?.pagination;
            if (pagination) {
                setCursor(pagination.endCursor);
                setStartCursor(pagination.startCursor);
                setHasMore(pagination.hasNextPage || false);
                setHasPrevious(pagination.hasPreviousPage || false);
                setTotalCount(pagination.total || 0);
                
                // Update cursor history for navigation (only when going forward)
                if (!isPrevious && !append && pagination.endCursor) {
                    setCursorHistory(prev => {
                        // Only add if it's not already the last item
                        if (prev.length === 0 || prev[prev.length - 1] !== pagination.endCursor) {
                            const newHistory = [...prev, pagination.endCursor];
                            // Keep only last 50 cursors to prevent memory issues
                            return newHistory.slice(-50);
                        }
                        return prev;
                    });
                }
            } else {
                setHasMore(newFeeds.length === 25);
                setHasPrevious(false);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setFeedsLoading(false);
            setIsLoadingMore(false);
        }
    };

    useEffect(() => {
        fetchFeeds(null, false);
        setCurrentPage(1);
        setCursorHistory([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refetch when search term changes
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setCurrentPage(1);
            setCursorHistory([]);
            fetchFeeds(null, false);
        }, 500); // Debounce search by 500ms

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    const handleNextPage = () => {
        if (cursor && hasMore) {
            setCurrentPage(prev => prev + 1);
            fetchFeeds(cursor, false, false);
        }
    };

    const handlePreviousPage = () => {
        if (cursorHistory.length > 1) {
            // Remove the last cursor (current page) and use the one before it
            const newHistory = [...cursorHistory];
            newHistory.pop(); // Remove current cursor
            const previousCursor = newHistory.length > 0 ? newHistory[newHistory.length - 1] : null;
            
            setCursorHistory(newHistory);
            setCurrentPage(prev => Math.max(1, prev - 1));
            
            if (previousCursor && previousCursor !== startCursor) {
                // Use the previous cursor
                fetchFeeds(previousCursor, false, true);
            } else {
                // Go to first page
                setCursorHistory([]);
                setCurrentPage(1);
                fetchFeeds(null, false, true);
            }
        } else {
            // Go to first page
            setCursorHistory([]);
            setCurrentPage(1);
            fetchFeeds(null, false, true);
        }
    };

    const handleFirstPage = () => {
        setCurrentPage(1);
        setCursorHistory([]);
        fetchFeeds(null, false, false);
    };

    // Removed infinite scroll observer - using pagination instead

    const handleViewDetails = (item) => {
        setSelectedIndicator(item);
        setShowDetailsModal(true);
    };

    const handleCloseModal = () => {
        setShowDetailsModal(false);
        setSelectedIndicator(null);
        setCopiedField(null);
    };

    const copyToClipboard = (text, fieldName) => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(text).then(() => {
                setCopiedField(fieldName);
                setTimeout(() => setCopiedField(null), 2000);
            });
        }
    };

    const getTLPInfo = (marking) => {
        if (!marking || !marking.definition) return null;
        
        const tlp = marking.definition.toUpperCase();
        switch (tlp) {
            case 'TLP:RED':
                return {
                    label: 'TLP:RED',
                    color: '#dc3545',
                    bgColor: '#f8d7da',
                    textColor: '#721c24',
                    description: 'Not for public distribution'
                };
            case 'TLP:AMBER':
                return {
                    label: 'TLP:AMBER',
                    color: '#ffc107',
                    bgColor: '#fff3cd',
                    textColor: '#856404',
                    description: 'Limited distribution within organization'
                };
            case 'TLP:GREEN':
                return {
                    label: 'TLP:GREEN',
                    color: '#198754',
                    bgColor: '#d1e7dd',
                    textColor: '#0f5132',
                    description: 'Share within security community'
                };
            case 'TLP:CLEAR':
            case 'TLP:WHITE':
                return {
                    label: 'TLP:CLEAR',
                    color: '#6c757d',
                    bgColor: '#f8f9fa',
                    textColor: '#495057',
                    description: 'Can be shared publicly'
                };
            default:
                return {
                    label: tlp,
                    color: '#6c757d',
                    bgColor: '#e9ecef',
                    textColor: '#495057',
                    description: 'Classification not specified'
                };
        }
    };

    const getObservableTypeIcon = (type) => {
        if (!type) return faGlobe;
        
        const lowerType = type.toLowerCase();
        if (lowerType.includes('url') || lowerType.includes('domain')) {
            return faLink;
        } else if (lowerType.includes('ip')) {
            return faNetworkWired;
        } else if (lowerType.includes('file') || lowerType.includes('hash')) {
            return faFile;
        } else {
            return faGlobe;
        }
    };

    const getScoreColor = (score) => {
        if (score >= 80) return '#dc3545'; // High - Red
        if (score >= 50) return '#ffc107'; // Medium - Yellow
        if (score >= 20) return '#0d6efd'; // Low - Blue
        return '#6c757d'; // Unknown - Gray
    };

    const getScoreLabel = (score) => {
        if (score >= 80) return 'High';
        if (score >= 50) return 'Medium';
        if (score >= 20) return 'Low';
        return 'Unknown';
    };

    // Get confidence score (use confidence field instead of x_opencti_score)
    const getConfidenceScore = (item) => {
        return item.x_opencti_score || item.confidence || 0;
    };

    // No need to filter on client side - API handles search
    const filteredFeeds = feeds;

    return (
        <div className="container-fluid">
            <div className="content-wrapper">
                <div className="block-header mb-4">
                    <h2 className="welcome-title">Threat Intelligence Feeds</h2>
                </div>

                {error && (
                    <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        <strong>Error:</strong> {error}
                    </Alert>
                )}

                {/* Enhanced Search Section */}
                <Card className="mb-4 shadow-sm" style={{ border: '1px solid #e9ecef', borderRadius: '12px' }}>
                    <Card.Body className="p-4">
                        <div className="d-flex align-items-center gap-3">
                            <div className="search-container flex-grow-1">
                                <label className="form-label small text-muted mb-2 fw-semibold">
                                    <FontAwesomeIcon icon={faSearch} className="me-1" />
                                    Search Indicators
                                </label>
                                <div className="input-group" style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #dee2e6' }}>
                                    <span className="input-group-text bg-white border-0" style={{ paddingLeft: '16px' }}>
                                        <FontAwesomeIcon icon={faSearch} className="text-muted" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-0"
                                        placeholder="Search by IP, domain, URL, hash, or any indicator value..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ fontSize: '0.95rem', padding: '12px 16px' }}
                                    />
                                    {searchTerm && (
                                        <button
                                            className="input-group-text bg-white border-0"
                                            onClick={() => setSearchTerm('')}
                                            style={{ cursor: 'pointer', color: '#6c757d', paddingRight: '16px' }}
                                            title="Clear search"
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    )}
                                </div>
                                {searchTerm && (
                                    <div className="mt-2">
                                        <small className="text-muted">
                                            Searching for: <strong>{searchTerm}</strong>
                                        </small>
                                    </div>
                                )}
                            </div>
                            {searchTerm && filteredFeeds.length > 0 && (
                                <div className="d-flex align-items-end">
                                    <Badge bg="primary" className="px-3 py-2" style={{ fontSize: '0.9rem', borderRadius: '8px' }}>
                                        {filteredFeeds.length} {filteredFeeds.length === 1 ? 'result' : 'results'}
                                    </Badge>
                                </div>
                            )}
                        </div>
                    </Card.Body>
                </Card>

                {feedsLoading ? (
                    <div className="text-center my-5 py-5">
                        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                        <p className="mt-3 text-muted">Loading threat intelligence indicators...</p>
                    </div>
                ) : (
                    <Card className="shadow-sm" style={{ border: '1px solid #e9ecef', borderRadius: '12px', overflow: 'hidden' }}>
                        <Card.Header className='d-flex align-items-center justify-content-between' style={{ 
                            background: 'linear-gradient(135deg, #0d6efd 0%, #0056b3 100%)', 
                            color: 'white',
                            padding: '20px 24px',
                            borderBottom: 'none'
                        }}>
                            <div className="d-flex align-items-center">
                                <div style={{ 
                                    width: '48px', 
                                    height: '48px', 
                                    borderRadius: '12px', 
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: '12px'
                                }}>
                                    <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: '1.5rem' }} />
                                </div>
                                <div>
                                    <h4 className='mb-0 fw-bold' style={{ color: 'white' }}>Threat Intelligence Indicators</h4>
                                    <small style={{ opacity: 0.9 }}> Threat Intelligence Feed</small>
                                </div>
                            </div>
                            {filteredFeeds.length > 0 && (
                                <Badge bg="light" text="dark" className="px-3 py-2 fw-semibold" style={{ fontSize: '0.9rem', borderRadius: '8px' }}>
                                    {filteredFeeds.length} {filteredFeeds.length === 1 ? 'Indicator' : 'Indicators'}
                                </Badge>
                            )}
                        </Card.Header>
                        <Card.Body className="p-0">
                            {filteredFeeds.length === 0 ? (
                                <div className="text-center py-5">
                                    <FontAwesomeIcon icon={faInfoCircle} className="text-muted mb-3" style={{ fontSize: '4rem', opacity: 0.5 }} />
                                    <h5 className="text-muted mb-2">No indicators found</h5>
                                    <p className="text-muted">
                                        {searchTerm ? 'Try adjusting your search terms or clear the search to see all indicators.' : 'No threat intelligence indicators available at this time.'}
                                    </p>
                                </div>
                            ) : (
                            <div className='table-responsive'>
                                    <table className="table table-hover table-custom mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '140px' }}>Type</th>
                                                <th style={{ width: '35%' }}>Indicator</th>
                                                <th style={{ width: '15%' }}>Confidence</th>
                                                <th style={{ width: '25%' }}>Tags</th>
                                                <th style={{ width: '15%' }}>Created</th>
                                                <th style={{ width: '10%' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredFeeds.map((item, index) => {
                                                const typeIcon = getObservableTypeIcon(item.x_opencti_main_observable_type);
                                                const confidenceScore = getConfidenceScore(item);
                                                const scoreColor = getScoreColor(confidenceScore);
                                                const scoreLabel = getScoreLabel(confidenceScore);
                                                
                                                return (
                                                    <tr 
                                                        key={item.id} 
                                                        className="table-row-hover"
                                                        onClick={() => handleViewDetails(item)}
                                                    >
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div 
                                                                    className="d-flex align-items-center justify-content-center me-2"
                                                                    style={{ 
                                                                        width: '36px', 
                                                                        height: '36px', 
                                                                        borderRadius: '8px',
                                                                        backgroundColor: '#e7f3ff'
                                                                    }}
                                                                >
                                                            <FontAwesomeIcon
                                                                        icon={typeIcon} 
                                                                        style={{ color: '#0d6efd', fontSize: '1.1rem' }}
                                                                    />
                                                                </div>
                                                                <span className="small fw-semibold text-muted">
                                                                    {item.x_opencti_main_observable_type?.split('-').pop() || 'Unknown'}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                                    <div>
                                                                <div className="fw-bold mb-1" style={{ color: '#212529', fontSize: '0.95rem' }}>
                                                                    {item.name || 'N/A'}
                                                                </div>
                                                                {item.description && (
                                                                    <div className="small text-muted" style={{ lineHeight: '1.4' }}>
                                                                        {item.description.length > 80 
                                                                            ? `${item.description.substring(0, 80)}...` 
                                                                            : item.description}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div 
                                                                    className="rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                                                                    style={{ 
                                                                        width: '45px', 
                                                                        height: '45px', 
                                                                        backgroundColor: `${scoreColor}15`,
                                                                        color: scoreColor,
                                                                        fontWeight: 'bold',
                                                                        fontSize: '0.95rem',
                                                                        border: `2px solid ${scoreColor}40`
                                                                    }}
                                                                    title={`Confidence: ${confidenceScore}% (${scoreLabel})`}
                                                                >
                                                                    {confidenceScore}
                                                                </div>
                                                                
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {item.createdBy ? (
                                                                <div>
                                                                    {/* <div className="d-flex align-items-center mb-2">
                                                                        <FontAwesomeIcon
                                                                            icon={faBuilding} 
                                                                            className="me-2" 
                                                                            style={{ color: '#6c757d', fontSize: '0.9rem' }} 
                                                                        />
                                                                        <span className="small fw-semibold" style={{ color: '#495057' }}>
                                                                            {item.createdBy.name || 'Unknown'}
                                                                        </span>
                                                                    </div> */}
                                                                    {item.objectLabel && item.objectLabel.length > 0 && (
                                                                        <div className="d-flex flex-wrap gap-1">
                                                                            {item.objectLabel.slice(0, 4).map((label, idx) => (
                                                                                <Badge 
                                                                                    key={idx}
                                                                                    style={{ 
                                                                                       color: '#FFFFFF',
                                                                                        fontSize: '0.7rem',
                                                                                        padding: '3px 8px',
                                                                                        fontWeight: '500',
                                                                                        borderRadius: '4px'
                                                                                    }}
                                                                                    title={label.value}
                                                                                >
                                                                                    {label.value}
                                                                                </Badge>
                                                                            ))}
                                                                            {item.objectLabel.length > 4 && (
                                                                                <Badge bg="secondary" style={{ fontSize: '0.7rem', padding: '3px 8px', borderRadius: '4px' }}>
                                                                                    +{item.objectLabel.length - 4}
                                                                                </Badge>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted small">Unknown Source</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <FontAwesomeIcon icon={faClock} className="me-2 text-muted" style={{ fontSize: '0.85rem' }} />
                                                                <div>
                                                                    <div className="small fw-semibold" style={{ color: '#495057' }}>
                                                                        {item.created ? moment(item.created).format('MMM DD, YYYY') : 'N/A'}
                                                                    </div>
                                                                    {item.valid_until && (
                                                                        <div className="small text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                                                                            Until: {moment(item.valid_until).format('MMM DD')}
                                                                    </div>
                                                                )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <button 
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleViewDetails(item);
                                                                }}
                                                                style={{ borderRadius: '6px' }}
                                                            >
                                                                <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                                                View
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                        })}
                                    </tbody>
                                </table>
                                </div>
                            )}
                            
                            {/* Pagination Controls */}
                            {filteredFeeds.length > 0 && !feedsLoading && (
                                <div className="border-top bg-light">
                                    <div className="d-flex align-items-center justify-content-between px-4 py-3">
                                        <div className="d-flex align-items-center">
                                            <span className="text-muted me-3">
                                                Showing <strong>{filteredFeeds.length}</strong> indicators
                                                {totalCount > 0 && (
                                                    <span> of <strong>{totalCount.toLocaleString()}</strong></span>
                                                )}
                                            </span>
                                        </div>
                                        
                                        <div className="d-flex align-items-center gap-2">
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={handleFirstPage}
                                                disabled={currentPage === 1 || feedsLoading}
                                                title="First Page"
                                            >
                                                <FontAwesomeIcon icon={faAngleDoubleLeft} />
                                            </button>
                                            
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={handlePreviousPage}
                                                disabled={currentPage === 1 || !hasPrevious || feedsLoading}
                                                title="Previous Page"
                                            >
                                                <FontAwesomeIcon icon={faChevronLeft} className="me-1" />
                                                Previous
                                            </button>
                                            
                                            <div className="px-3 py-1 bg-white border rounded" style={{ minWidth: '100px', textAlign: 'center' }}>
                                                <span className="fw-semibold text-primary">
                                                    Page {currentPage}
                                                </span>
                                            </div>
                                            
                                    <button 
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={handleNextPage}
                                                disabled={!hasMore || feedsLoading}
                                                title="Next Page"
                                            >
                                                Next
                                                <FontAwesomeIcon icon={faChevronRight} className="ms-1" />
                                    </button>
                                            
                                            {isLoadingMore && (
                                                <Spinner animation="border" size="sm" variant="primary" className="ms-2" />
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Page Info */}
                                    <div className="px-4 pb-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="small text-muted">
                                                {totalCount > 0 && (
                                                    <span>
                                                        Page {currentPage} 
                                                        {Math.ceil(totalCount / 25) > 0 && (
                                                            <span> of approximately {Math.ceil(totalCount / 25)}</span>
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="small text-muted">
                                                25 indicators per page
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Loading indicator */}
                            {isLoadingMore && (
                                <div className="text-center py-3 border-top bg-light">
                                    <Spinner animation="border" size="sm" variant="primary" />
                                    <span className="ms-2 text-muted">Loading indicators...</span>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                )}
            </div>

            {/* Details Modal */}
            <Modal 
                show={showDetailsModal} 
                onHide={handleCloseModal} 
                size="lg"
                centered
            >
                <Modal.Header closeButton style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                    <Modal.Title className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faShieldAlt} className="me-2 text-primary" />
                        Indicator Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {selectedIndicator && (
                        <div>
                            {/* Header Section */}
                            <div className="mb-4 pb-3 border-bottom">
                                <div className="d-flex align-items-start justify-content-between mb-3">
                                    <div className="flex-grow-1">
                                        <h5 className="fw-bold mb-2" style={{ color: '#212529' }}>
                                            {selectedIndicator?.name || 'N/A'}
                                        </h5>
                                        {selectedIndicator?.description && (
                                            <p className="text-muted mb-0" style={{ lineHeight: '1.6' }}>
                                                {selectedIndicator?.description}
                                            </p>
                                        )}
                                    </div>
                                    <div className="ms-3">
                                        {(() => {
                                            const confidenceScore = getConfidenceScore(selectedIndicator);
                                            const scoreColor = getScoreColor(confidenceScore);
                                            const scoreLabel = getScoreLabel(confidenceScore);
                                            return (
                                                <div className="text-center">
                                                    <div 
                                                        className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                                                        style={{ 
                                                            width: '70px', 
                                                            height: '70px', 
                                                            backgroundColor: `${scoreColor}15`,
                                                            color: scoreColor,
                                                            fontWeight: 'bold',
                                                            fontSize: '1.1rem',
                                                            border: `3px solid ${scoreColor}40`
                                                        }}
                                                    >
                                                        {confidenceScore}%
                                                    </div>
                                                    <div className="small fw-semibold" style={{ color: scoreColor }}>
                                                        {scoreLabel} Confidence
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                                
                                <div className="d-flex flex-wrap gap-2">
                                    <Badge 
                                        bg="" 
                                        className="px-3 py-2"
                                        style={{ fontSize: '0.85rem' }}
                                    >
                                        <FontAwesomeIcon icon={getObservableTypeIcon(selectedIndicator.x_opencti_main_observable_type)} className="me-1" />
                                        {selectedIndicator.x_opencti_main_observable_type || 'Unknown'}
                                    </Badge>
                                </div>
                            </div>

                            {/* Details Grid */}
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="detail-item p-3 border rounded">
                                        <div className="small text-muted mb-1">Indicator ID</div>
                                        <div className="d-flex align-items-center justify-content-between">
                                            <code className="small">{selectedIndicator.id}</code>
                                            <button
                                                className="btn btn-sm btn-outline-secondary ms-2"
                                                onClick={() => copyToClipboard(selectedIndicator.id, 'id')}
                                                title="Copy ID"
                                            >
                                                <FontAwesomeIcon icon={copiedField === 'id' ? faCheckCircle : faCopy} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="col-md-6">
                                    <div className="detail-item p-3 border rounded">
                                        <div className="small text-muted mb-1">Standard ID</div>
                                        <div className="d-flex align-items-center justify-content-between">
                                            <code className="small">{selectedIndicator?.standard_id || 'N/A'}</code>
                                            {selectedIndicator?.standard_id && (
                                                <button
                                                    className="btn btn-sm btn-outline-secondary ms-2"
                                                    onClick={() => copyToClipboard(selectedIndicator?.standard_id, 'standard_id')}
                                                    title="Copy Standard ID"
                                                >
                                                    <FontAwesomeIcon icon={copiedField === 'standard_id' ? faCheckCircle : faCopy} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-12">
                                    <div className="detail-item p-3 border rounded">
                                        <div className="small text-muted mb-2">Pattern</div>
                                        <div className="d-flex align-items-center justify-content-between">
                                            <code className="small text-break" style={{ maxWidth: '85%' }}>
                                                {selectedIndicator?.pattern || 'N/A'}
                                            </code>
                                            {selectedIndicator?.pattern && (
                                                <button
                                                    className="btn btn-sm btn-outline-secondary ms-2"
                                                    onClick={() => copyToClipboard(selectedIndicator?.pattern, 'pattern')}
                                                    title="Copy Pattern"
                                                >
                                                    <FontAwesomeIcon icon={copiedField === 'pattern' ? faCheckCircle : faCopy} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="detail-item p-3 border rounded">
                                        <div className="small text-muted mb-1">Source Organization</div>
                                        <div className="d-flex align-items-center">
                                            <FontAwesomeIcon icon={faBuilding} className="me-2 text-muted" />
                                            <span className="fw-semibold">
                                                {selectedIndicator?.createdBy?.name || 'Unknown'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="detail-item p-3 border rounded">
                                        <div className="small text-muted mb-1">Confidence Score</div>
                                        <div className="d-flex align-items-center">
                                            <div className="fw-semibold me-2" style={{ fontSize: '1.1rem' }}>
                                                {getConfidenceScore(selectedIndicator)}
                                            </div>
                                            <Badge 
                                                style={{ 
                                                    //backgroundColor: `${getScoreColor(getConfidenceScore(selectedIndicator))}15`,
                                                    fontSize: '0.75rem',
                                                    padding: '3px 8px',
                                                    color: 'white'
                                                }}
                                            >
                                                {getScoreLabel(getConfidenceScore(selectedIndicator))}</Badge>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="detail-item p-3 border rounded">
                                        <div className="small text-muted mb-1">Created</div>
                                        <div className="d-flex align-items-center">
                                            <FontAwesomeIcon icon={faClock} className="me-2 text-muted" />
                                            <span>
                                                {selectedIndicator.created 
                                                    ? moment(selectedIndicator.created).format('MMMM DD, YYYY [at] HH:mm') 
                                                    : 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="detail-item p-3 border rounded">
                                        <div className="small text-muted mb-1">Last Modified</div>
                                        <div className="d-flex align-items-center">
                                            <FontAwesomeIcon icon={faClock} className="me-2 text-muted" />
                                            <span>
                                                {selectedIndicator?.modified 
                                                    ? moment(selectedIndicator?.modified).format('MMMM DD, YYYY [at] HH:mm') 
                                                    : 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {selectedIndicator.valid_from && (
                                    <div className="col-md-6">
                                        <div className="detail-item p-3 border rounded">
                                            <div className="small text-muted mb-1">Valid From</div>
                                            <div>
                                                {moment(selectedIndicator?.valid_from).format('MMMM DD, YYYY')}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {selectedIndicator.valid_until && (
                                    <div className="col-md-6">
                                        <div className="detail-item p-3 border rounded">
                                            <div className="small text-muted mb-1">Valid Until</div>
                                            <div>
                                                {moment(selectedIndicator.valid_until).format('MMMM DD, YYYY')}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {selectedIndicator.objectLabel && selectedIndicator.objectLabel.length > 0 && (
                                    <div className="col-md-12">
                                        <div className="detail-item p-3 border rounded">
                                            <div className="small text-muted mb-2">
                                                <FontAwesomeIcon icon={faTag} className="me-1" />
                                                Labels ({selectedIndicator.objectLabel.length})
                                            </div>
                                            <div className="d-flex flex-wrap gap-2">
                                                {selectedIndicator.objectLabel.map((label, idx) => (
                                                    <Badge 
                                                        key={idx}
                                                        style={{ 
                                                            backgroundColor: label.color || '#6c757d',
                                                            fontSize: '0.8rem',
                                                            padding: '5px 10px',
                                                            fontWeight: '500',
                                                            color: '#FFFFFF'
                                                        }}
                                                    >
                                                        {label.value}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer style={{ backgroundColor: '#f8f9fa', borderTop: '2px solid #dee2e6' }}>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
            
            <style jsx>{`
                .table-custom {
                    font-size: 0.9rem;
                }
                .table-custom td {
                    vertical-align: middle;
                    padding: 16px 12px;
                }
                .table-row-hover {
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .table-row-hover:hover {
                    background-color: #f8f9fa !important;
                    transform: translateX(2px);
                    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                }
                .table-custom thead th {
                    font-weight: 600;
                    text-transform: uppercase;
                    font-size: 0.8rem;
                    letter-spacing: 0.5px;
                    border-bottom: 2px solid #dee2e6;
                    background-color: #f8f9fa;
                }
                .search-container {
                    max-width: 100%;
                }
                .detail-item {
                    background-color: #f8f9fa;
                    transition: all 0.2s ease;
                }
                .detail-item:hover {
                    background-color: #e9ecef;
                    border-color: #0d6efd !important;
                }
                code {
                    background-color: #f1f3f5;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 0.85rem;
                }
            `}</style>
        </div>
    );
};

export default ThreatIntelFeeds; 
